import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Phone, MessageSquare, CheckCircle, Clock, Shield, DollarSign, Wrench, Home, Settings, RefreshCw, AlertCircle, Anchor, Square, AlignJustify, Wind, ShieldCheck, Building } from 'lucide-react';
import { Button } from '../components/ui/button';
import { businessInfo } from '../data/mock';

const ServicesPage = () => {
  const services = [
    {
      id: 1,
      icon: Home,
      title: "Garage Door Installation",
      description: "Professional installation of new residential and commercial garage doors with warranty.",
      link: "/installation",
      features: [
        "Free in-home consultation",
        "Premium door selection",
        "Professional measurement",
        "Expert installation",
        "Post-installation inspection",
        "Warranty included"
      ]
    },
    {
      id: 2,
      icon: Wrench,
      title: "Garage Door Repair",
      description: "Fast and reliable repair services for all makes and models of garage doors.",
      link: "/repair",
      features: [
        "Same-day service available",
        "All brands serviced",
        "Diagnostic inspection",
        "Quality replacement parts",
        "90-day repair warranty",
        "Upfront pricing"
      ]
    },
    {
      id: 3,
      icon: Settings,
      title: "Opener Installation & Repair",
      description: "Expert installation and repair of garage door openers from leading brands.",
      link: "/opener-service",
      features: [
        "Smart opener options",
        "Belt, chain, or screw drive",
        "Wi-Fi connectivity available",
        "Safety sensor installation",
        "Programming included",
        "Smartphone app setup"
      ]
    },
    {
      id: 4,
      icon: RefreshCw,
      title: "Spring Replacement",
      description: "Safe and efficient replacement of broken or worn garage door springs.",
      link: "/spring-replacement",
      features: [
        "Emergency service available",
        "High-cycle springs",
        "Torsion & extension springs",
        "Safety cable installation",
        "Balance adjustment",
        "Lifetime spring warranty option"
      ]
    },
    {
      id: 5,
      icon: CheckCircle,
      title: "Preventive Maintenance",
      description: "Regular maintenance services to keep your garage door operating smoothly.",
      link: "/maintenance",
      features: [
        "20-point inspection",
        "Lubrication of moving parts",
        "Safety system test",
        "Track cleaning & alignment",
        "Hardware tightening",
        "Detailed service report"
      ]
    },
    {
      id: 6,
      icon: AlertCircle,
      title: "Emergency Services",
      description: "24/7 emergency repair services when you need us most.",
      link: "/emergency",
      features: [
        "Available 24/7/365",
        "1-2 hour response time",
        "Fully stocked service trucks",
        "On-the-spot repairs",
        "No extra emergency fees",
        "Same-day service guarantee"
      ]
    },
    {
      id: 7,
      icon: Anchor,
      title: "Cable Repair & Replacement",
      description: "Professional cable repair and replacement to ensure safe door operation.",
      link: "/cables",
      features: [
        "High-strength cables",
        "Safety inspection included",
        "Proper tension adjustment",
        "Both cables replaced",
        "Wear prevention tips",
        "Quality guaranteed"
      ]
    },
    {
      id: 8,
      icon: Square,
      title: "Panel Replacement",
      description: "Replace damaged panels to restore your door's appearance and function.",
      features: [
        "Color matching available",
        "Single or multiple panels",
        "Weather seal replacement",
        "Insulation upgrade option",
        "Professional installation",
        "Finish touch-up included"
      ]
    },
    {
      id: 9,
      icon: AlignJustify,
      title: "Track Alignment",
      description: "Precision track alignment for smooth and quiet door operation.",
      features: [
        "Track straightening",
        "Roller replacement",
        "Proper spacing adjustment",
        "Mounting bracket repair",
        "Smooth operation test",
        "Noise reduction"
      ]
    },
    {
      id: 10,
      icon: Wind,
      title: "Weatherstripping",
      description: "Seal your garage from the elements with quality weatherstripping.",
      features: [
        "Bottom seal replacement",
        "Side & top sealing",
        "Energy savings",
        "Pest prevention",
        "Temperature control",
        "Premium materials"
      ]
    },
    {
      id: 11,
      icon: ShieldCheck,
      title: "Safety Inspections",
      description: "Comprehensive safety inspections to ensure your door meets all standards.",
      features: [
        "Complete safety audit",
        "Auto-reverse test",
        "Photo eye alignment",
        "Emergency release check",
        "Hardware safety review",
        "Detailed report provided"
      ]
    },
    {
      id: 12,
      icon: Building,
      title: "Commercial Services",
      description: "Specialized services for commercial and industrial garage doors.",
      features: [
        "Rolling steel doors",
        "High-speed doors",
        "Loading dock equipment",
        "Scheduled maintenance",
        "24/7 commercial support",
        "Volume discounts available"
      ]
    }
  ];

  const whyChooseUs = [
    {
      icon: Clock,
      title: "Fast Response",
      description: "Same-day service available for most repairs"
    },
    {
      icon: Shield,
      title: "Quality Guaranteed",
      description: "All work backed by our satisfaction guarantee"
    },
    {
      icon: DollarSign,
      title: "Fair Pricing",
      description: "Competitive rates with no hidden fees"
    },
    {
      icon: CheckCircle,
      title: "Expert Technicians",
      description: "Licensed, insured, and experienced professionals"
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50">
      {/* Hero */}
      <section className="pt-32 pb-12 bg-gradient-to-r from-blue-900 to-blue-800">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center text-white">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              Professional Garage Door Services
            </h1>
            <p className="text-xl text-blue-100 mb-6">
              Installation • Repair • Maintenance • Emergency Service
            </p>
            <p className="text-lg text-blue-200 mb-8">
              Serving Saskatchewan with premium garage door solutions since day one
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Button 
                onClick={() => window.location.href = `tel:${businessInfo.phone}`}
                size="lg"
                className="bg-green-600 hover:bg-green-700 text-white font-bold px-8 py-6 text-lg"
              >
                <Phone size={20} className="mr-2" />
                Call {businessInfo.phone}
              </Button>
              <Button 
                onClick={() => window.location.href = '/quote'}
                size="lg"
                variant="outline"
                className="border-2 border-white text-white hover:bg-white hover:text-blue-900 font-bold px-8 py-6 text-lg"
              >
                <MessageSquare size={20} className="mr-2" />
                Get Free Quote
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Why Choose 306 Garage Doors?</h2>
            <p className="text-xl text-gray-600">Professional service you can trust</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            {whyChooseUs.map((item, idx) => {
              const IconComponent = item.icon;
              return (
                <Card key={idx} className="text-center border-2 hover:border-blue-600 hover:shadow-xl transition-all">
                  <CardHeader>
                    <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <IconComponent size={32} className="text-blue-700" />
                    </div>
                    <CardTitle className="text-xl mb-2">{item.title}</CardTitle>
                    <CardDescription className="text-base">{item.description}</CardDescription>
                  </CardHeader>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Complete Service Offerings</h2>
            <p className="text-xl text-gray-600">Expert solutions for all your garage door needs</p>
          </div>

          <div className="grid lg:grid-cols-2 gap-6 max-w-7xl mx-auto">
            {services.map((service) => {
              const IconComponent = service.icon;
              return (
                <Card key={service.id} className="border-2 border-gray-200 hover:border-blue-600 hover:shadow-xl transition-all">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4 mb-4">
                      <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                        <IconComponent size={28} className="text-blue-700" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-2xl font-bold text-gray-900 mb-2">{service.title}</h3>
                        <p className="text-gray-600 mb-3">{service.description}</p>
                      </div>
                    </div>

                    <div className="mb-4">
                      <h4 className="font-bold text-gray-900 mb-3">Service Includes:</h4>
                      <div className="grid grid-cols-2 gap-2">
                        {service.features.map((feature, idx) => (
                          <div key={idx} className="flex items-start gap-2">
                            <CheckCircle size={16} className="text-green-600 flex-shrink-0 mt-0.5" />
                            <span className="text-gray-700 text-sm">{feature}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="flex gap-3">
                      <Button 
                        onClick={() => window.location.href = service.link}
                        size="sm"
                        className="bg-orange-500 hover:bg-orange-600 text-white flex-1 font-bold"
                      >
                        Learn More
                      </Button>
                      <Button 
                        onClick={() => window.location.href = '/quote'}
                        size="sm"
                        className="bg-blue-800 hover:bg-blue-900 text-white flex-1 font-bold"
                      >
                        Get Quote
                      </Button>
                      <Button 
                        onClick={() => window.location.href = `tel:${businessInfo.phone}`}
                        size="sm"
                        variant="outline"
                        className="border-2 border-green-600 text-green-700 hover:bg-green-600 hover:text-white flex-1 font-bold"
                      >
                        <Phone size={16} className="mr-1" />
                        Call
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Emergency CTA */}
      <section className="py-20 bg-gradient-to-r from-red-600 to-red-700">
        <div className="container mx-auto px-4 text-center text-white">
          <AlertCircle size={64} className="mx-auto mb-6 animate-pulse" />
          <h2 className="text-4xl font-bold mb-6">Need Emergency Service?</h2>
          <p className="text-xl text-red-100 mb-8 max-w-3xl mx-auto">
            We're available 24/7 for garage door emergencies. Fast response within 1-2 hours.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Button 
              onClick={() => window.location.href = `tel:${businessInfo.phone}`}
              size="lg"
              className="bg-white text-red-700 hover:bg-gray-100 font-bold px-12 py-8 text-xl shadow-2xl"
            >
              <Phone size={24} className="mr-2" />
              Call Now: {businessInfo.phone}
            </Button>
            <Button 
              onClick={() => window.location.href = '/emergency'}
              size="lg"
              variant="outline"
              className="border-2 border-white text-white hover:bg-white hover:text-red-700 font-bold px-12 py-8 text-xl"
            >
              Learn More
            </Button>
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-20 bg-gradient-to-r from-blue-900 to-blue-800">
        <div className="container mx-auto px-4 text-center text-white">
          <h2 className="text-4xl font-bold mb-6">Ready to Get Started?</h2>
          <p className="text-xl text-blue-100 mb-8 max-w-3xl mx-auto">
            Request your free quote today or call us to discuss your garage door needs.
          </p>
          <Button 
            onClick={() => window.location.href = '/quote'}
            size="lg"
            className="bg-green-600 hover:bg-green-700 text-white font-bold px-12 py-8 text-lg"
          >
            Request Free Quote
          </Button>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;